import { useMemo } from 'react';
import { supabase } from '../lib/supabase';
import type {
  PilotRating,
  PilotRatingType,
  PilotRatingStatus,
} from '../types/pilot';

export type PilotTarget =
  | { kind: 'user'; userId: string }
  | { kind: 'org_pilot'; orgPilotId: string };

export interface CreatePilotRatingInput {
  target: PilotTarget;
  rating_type: PilotRatingType | string;
  rating_code: string;
  aircraft_type_or_class?: string | null;
  issue_date?: string | null;
  expiry_date?: string | null;
  revalidation_date?: string | null;
  status?: PilotRatingStatus;
  remarks?: string | null;
}

export type UpdatePilotRatingInput = Partial<Omit<CreatePilotRatingInput, 'target'>>;

/**
 * Data access for pilot licence ratings (migration 165). Supports both
 * registered users and ad-hoc org_pilots via a discriminated target.
 *
 * The returned object is memoised so callers can safely put it in
 * `useEffect` / `useCallback` dependency arrays without triggering loops.
 */
export function usePilotRatings() {
  return useMemo(() => {
  async function list(target: PilotTarget): Promise<PilotRating[]> {
    const query = supabase.from('pilot_ratings').select('*');
    const filtered =
      target.kind === 'user'
        ? query.eq('user_id', target.userId)
        : query.eq('org_pilot_id', target.orgPilotId);

    const { data, error } = await filtered
      .order('expiry_date', { ascending: true, nullsFirst: false });

    if (error) throw error;
    return (data ?? []) as PilotRating[];
  }

  async function create(input: CreatePilotRatingInput): Promise<PilotRating> {
    const row = {
      user_id: input.target.kind === 'user' ? input.target.userId : null,
      org_pilot_id: input.target.kind === 'org_pilot' ? input.target.orgPilotId : null,
      rating_type: input.rating_type,
      rating_code: input.rating_code,
      aircraft_type_or_class: input.aircraft_type_or_class ?? null,
      issue_date: input.issue_date ?? null,
      expiry_date: input.expiry_date ?? null,
      revalidation_date: input.revalidation_date ?? null,
      status: input.status ?? 'unknown',
      remarks: input.remarks ?? null,
    };

    const { data, error } = await supabase
      .from('pilot_ratings')
      .insert(row)
      .select('*')
      .single();

    if (error) throw error;
    return data as PilotRating;
  }

  async function update(id: string, input: UpdatePilotRatingInput): Promise<PilotRating> {
    const { data, error } = await supabase
      .from('pilot_ratings')
      .update(input)
      .eq('id', id)
      .select('*')
      .single();

    if (error) throw error;
    return data as PilotRating;
  }

  async function remove(id: string): Promise<void> {
    const { error } = await supabase.from('pilot_ratings').delete().eq('id', id);
    if (error) throw error;
  }

  /**
   * M:M covered aircraft types (pilot_rating_aircraft_types).
   * Returns fleet_model_ids linked to this rating.
   */
  async function listCoveredAircraftTypes(ratingId: string): Promise<string[]> {
    const { data, error } = await supabase
      .from('pilot_rating_aircraft_types')
      .select('fleet_model_id')
      .eq('rating_id', ratingId);

    if (error) throw error;
    return (data ?? []).map((r) => (r as { fleet_model_id: string }).fleet_model_id);
  }

  async function addAircraftType(ratingId: string, fleetModelId: string): Promise<void> {
    const { error } = await supabase
      .from('pilot_rating_aircraft_types')
      .insert({ rating_id: ratingId, fleet_model_id: fleetModelId });
    if (error) throw error;
  }

  async function removeAircraftType(ratingId: string, fleetModelId: string): Promise<void> {
    const { error } = await supabase
      .from('pilot_rating_aircraft_types')
      .delete()
      .eq('rating_id', ratingId)
      .eq('fleet_model_id', fleetModelId);
    if (error) throw error;
  }

    return {
      list,
      create,
      update,
      remove,
      listCoveredAircraftTypes,
      addAircraftType,
      removeAircraftType,
    };
  }, []);
}
